import { useState } from 'react';

import { Button, Divider, Grid, Menu, MenuItem, Stack } from '@mui/material';

import TowRecordImage from '@/assets/images/pngs/bolo.png';
import { DataGrid, SideModal, ViewItem } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { SideModalTitle } from '@/features/properties/components/sidemodal-content/SideModalTitle';
import {
  towingRecordDummyData,
  TowingRecordsTableColumns,
} from '@/pages/Tools/TowingManagement/data';
import { TowRecordFormPanel } from '@/pages/Tools/TowingManagement/Panels/TowRecordsSidePanel';
import { useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

interface TowingRecordsTableProps {
  page: number;
  onChangePage: (newPage: number) => void;
}

const mapTowType = (type: string): string => {
  switch (type) {
    case 'Police':
      return 'police-initiated';
    case 'EMS':
      return 'ems';
    case 'Fire':
      return 'fire';
    case 'Private':
      return 'private';
    default:
      return '';
  }
};

const mapCompanyName = (company: string): string => {
  return company.split(' · ')[0];
};

const mapStatus = (status: string): string => {
  switch (status) {
    case 'Completed':
      return 'completed';
    case 'Not assigned':
      return 'notAssigned';
    case 'In progress':
      return 'inProgress';
    case 'Released':
      return 'released';
    default:
      return '';
  }
};

const mapDateTime = (date: string): string => {
  return date;
};

const getEmptyStringIfUndefined = (value?: string): string => {
  return value || '';
};

export const TowingRecordsTable = ({ page }: TowingRecordsTableProps) => {
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedTowRecord, setSelectedTowRecord] = useState<any>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [towRecordToEdit, setTowRecordToEdit] = useState<any | null>(null);
  const openMenu = Boolean(anchorEl);
  const { navigateWithSearch } = useSearchPersist();

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTowRecord(row);
  };

  const { user } = useUserStore();

  const handleMenuClose = () => setAnchorEl(null);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `towingRecords-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return TowingRecordsTableColumns;
      }

      return mergeColumnSettings(TowingRecordsTableColumns, savedSettings);
    },
  );

  const columns = useDataGridColumns({
    type: 'towingRecords',
    columnSettings,
    openMenu: handleMenuOpen,
  });

  return (
    <Stack spacing={2} flex={1}>
      <DataGrid
        key={page}
        name="towingRecords"
        persistSearch
        clientMode
        clientRows={towingRecordDummyData}
        columns={columns}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        onRowClick={(params) =>
          navigateWithSearch(
            `/tools/towing-management/tow-record/${params.row.id}`,
          )
        }
        autoPageSize
        getRowId={(row) => row.id}
        cta={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setTowRecordToEdit(null);
              setFormOpen(true);
            }}
          >
            Add tow record
          </Button>
        }
      />

      <Divider sx={{ mt: 2 }} />

      <SideModal open={openViewModal} onClose={() => setOpenViewModal(false)}>
        <>
          <Stack sx={{ p: 2, pt: 0 }}>
            <SideModalTitle>Towing Record Details</SideModalTitle>
          </Stack>
          <Grid container spacing={2} sx={{ p: 2, pt: 0 }}>
            <Grid item xs={5}>
              <img
                src={TowRecordImage}
                alt="Tow record"
                style={{ width: '100%', height: '300px' }}
              />
            </Grid>
            <Grid item xs={7}>
              <ViewItem label="Tow Type" value={selectedTowRecord.type} />
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <ViewItem label="Status" value={selectedTowRecord.status} />
                </Grid>
                <Grid item xs={4}>
                  <ViewItem
                    label="Date and Time"
                    value={selectedTowRecord.date}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ViewItem
                    label="Initiator"
                    value={selectedTowRecord.initiator}
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 1 }} />
              <ViewItem label="Vehicle" value={selectedTowRecord.vehicle} />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <ViewItem
                label="Reason"
                value={selectedTowRecord.reason}
                sx={{ '.value': { textTransform: 'none', flex: 'unset' } }}
              />
            </Grid>
          </Grid>

          <Stack sx={{ p: 2, mt: 'auto' }}>
            <Divider sx={{ my: 3 }} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setOpenViewModal(false)}
              >
                Close
              </Button>
            </Stack>
          </Stack>
        </>
      </SideModal>

      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{ 'aria-labelledby': 'actions-menu-button' }}
      >
        <MenuItem
          onClick={() => {
            navigateWithSearch(
              `/tools/towing-management/tow-record/${selectedTowRecord.id}`,
            );
            handleMenuClose();
          }}
        >
          View
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTowRecordToEdit({
              incidentNumber: selectedTowRecord.incident,
              dateTime: mapDateTime(selectedTowRecord.date),
              towType: mapTowType(selectedTowRecord.type),
              officerName: selectedTowRecord.initiator,
              badgeNumber: getEmptyStringIfUndefined(
                selectedTowRecord.badgeNumber,
              ),
              licensePlate: getEmptyStringIfUndefined(
                selectedTowRecord.licensePlate,
              ),
              vin: getEmptyStringIfUndefined(selectedTowRecord.vin),
              year: getEmptyStringIfUndefined(selectedTowRecord.year),
              make: getEmptyStringIfUndefined(selectedTowRecord.make),
              model: getEmptyStringIfUndefined(selectedTowRecord.model),
              color: getEmptyStringIfUndefined(selectedTowRecord.color),
              towFromLocation: getEmptyStringIfUndefined(
                selectedTowRecord.towFromLocation,
              ),
              destination: getEmptyStringIfUndefined(
                selectedTowRecord.destination,
              ),
              towingCompany: mapCompanyName(selectedTowRecord.company),
              status: mapStatus(selectedTowRecord.status),
              reason: getEmptyStringIfUndefined(selectedTowRecord.reason),
              estimatedCost: getEmptyStringIfUndefined(
                selectedTowRecord.estimatedCost,
              ),
              actualCost: getEmptyStringIfUndefined(
                selectedTowRecord.actualCost,
              ),
              additionalNotes: getEmptyStringIfUndefined(
                selectedTowRecord.additionalNotes,
              ),
            });
            setFormOpen(true);
            setOpenViewModal(false);
            handleMenuClose();
          }}
        >
          Edit
        </MenuItem>
      </Menu>

      <TowRecordFormPanel
        open={isFormOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={() => {
          // eslint-disable-next-line no-console
          setFormOpen(false);
        }}
        defaultValues={towRecordToEdit || undefined}
        mode={towRecordToEdit ? 'edit' : 'add'}
      />
    </Stack>
  );
};
